import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import './CommonStyles.css';
import './AddSellStyles.css';
import './ProductSearchStyles.css';
import dateUtils from '../../utils/dateUtils';

const today = () => dateUtils.getChicagoISOString().split('T')[0];

const AddPurchaseRequisition = ({ onBack }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    brand_id: '',
    category_id: '',
    location_id: '',
    ref_no: '',
    delivery_date: today(),
    products: [],
  });
  const [errors, setErrors] = useState({});
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productOptions, setProductOptions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const normalizeOptions = (value) => {
    if (Array.isArray(value)) return value.map((item) => (typeof item === 'object' ? item : { id: item, name: item }));
    if (value && typeof value === 'object') {
      return Object.entries(value).map(([id, name]) => ({ id, name }));
    }
    return [];
  };

  const handleChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const fetchCreateData = async () => {
    try {
      const result = await apiService.getPurchaseRequisitionCreateData();
      if (result.success) {
        const data = result.data || {};
        setLocations(normalizeOptions(data.business_locations));
        setCategories(normalizeOptions(data.categories));
        setBrands(normalizeOptions(data.brands));
      }
    } catch (err) {
      /* console.error('Error fetching purchase requisition create data:', err); */
    }
  };

  const fetchProducts = async () => {
    if (!form.location_id) {
      setProductOptions([]);
      return;
    }
    setIsLoading(true);
    try {
      const params = { location_id: form.location_id };
      if (form.category_id) params.category_id = form.category_id;
      if (form.brand_id) params.brand_id = form.brand_id;
      if (productSearchTerm) params.search = productSearchTerm;
      const result = await apiService.getPurchaseRequisitionProducts(params);
      const list = Array.isArray(result) ? result : (result.data || []);
      setProductOptions(list);
    } catch (err) {
      /* console.error('Error fetching requisition products:', err); */
      setProductOptions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCreateData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchProducts(), 400);
    return () => clearTimeout(timer);
  }, [form.location_id, form.category_id, form.brand_id, productSearchTerm]);

  const filteredOptions = productOptions.filter((p) => {
    const term = productSearchTerm.trim().toLowerCase();
    if (!term) return true;
    return (
      (p.product || '').toLowerCase().includes(term) ||
      (p.sku || '').toLowerCase().includes(term) ||
      (p.sub_sku || '').toLowerCase().includes(term)
    );
  });

  const addProductRow = (product) => {
    const exists = form.products.find((p) => p.variation_id === product.variation_id);
    if (exists) return;
    setForm((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        {
          id: Date.now(),
          product_id: product.product_id,
          variation_id: product.variation_id,
          name: product.product || product.name || '',
          sku: product.sub_sku || product.sku || '',
          unit: product.unit || '',
          alertQuantity: product.alert_quantity || 0,
          requiredQuantity: 1,
          secondary_unit_quantity: 0,
        },
      ],
    }));
    setProductSearchTerm('');
  };

  const updateProduct = (id, key, value) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.map((p) => (p.id !== id ? p : { ...p, [key]: value })),
    }));
  };

  const removeProduct = (id) => setForm((prev) => ({ ...prev, products: prev.products.filter((p) => p.id !== id) }));

  const validate = () => {
    const newErrors = {};
    if (!form.location_id) newErrors.location_id = 'Business location is required';
    if (!form.delivery_date) newErrors.delivery_date = 'Required by date is required';
    if (form.products.length === 0) newErrors.products = 'Please add at least one product';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => ({
    location_id: form.location_id,
    ref_no: form.ref_no,
    delivery_date: form.delivery_date,
    purchases: form.products.map((p) => ({
      product_id: p.product_id,
      variation_id: p.variation_id,
      quantity: p.requiredQuantity,
      secondary_unit_quantity: p.secondary_unit_quantity || 0,
    })),
  });

  const handleSave = async (shouldPrint = false) => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const result = await apiService.createPurchaseRequisition(buildPayload());
      if (result.success) {
        toast.success('Purchase requisition saved successfully.');
        if (shouldPrint) {
          window.open(`/purchase-requisition/print/${result.data?.id || result.data?.transaction_id}`, '_blank');
        }
        if (onBack) onBack();
        else navigate('/purchases/requisition');
      } else {
        toast.error(result.message || 'Failed to save purchase requisition');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to save purchase requisition');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="sell-form-container add-sell-override">
      <div className="page-header">
        <button
          type="button"
          onClick={onBack || (() => navigate('/purchases/requisition'))}
          style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px', height: '40px' }}
          title="Back to Purchases"
        >←</button>
        <h2 style={{ fontSize: '1.25rem' }}>Add Purchase Requisition</h2>
      </div>

      <form className="sell-form" onSubmit={(e) => { e.preventDefault(); handleSave(false); }}>
        <div className="form-section-content">
          <div className="form-row">
            <div className="form-group">
              <label>Brand:</label>
              <select
                className="form-select"
                value={form.brand_id}
                onChange={(e) => handleChange('brand_id', e.target.value)}
              >
                <option value="">All Brands</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Category:</label>
              <select
                className="form-select"
                value={form.category_id}
                onChange={(e) => handleChange('category_id', e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Business Location:</label>
              <select
                className={`form-select ${errors.location_id ? 'error' : ''}`}
                value={form.location_id}
                onChange={(e) => handleChange('location_id', e.target.value)}
              >
                <option value="">Please Select</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
              {errors.location_id && <span className="error-text">{errors.location_id}</span>}
            </div>

            <div className="form-group">
              <label>Reference No:</label>
              <input
                type="text"
                className="form-input"
                value={form.ref_no}
                onChange={(e) => handleChange('ref_no', e.target.value)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Required by date:</label>
              <input
                type="date"
                className={`form-input ${errors.delivery_date ? 'error' : ''}`}
                value={form.delivery_date}
                onChange={(e) => handleChange('delivery_date', e.target.value)}
              />
              {errors.delivery_date && <span className="error-text">{errors.delivery_date}</span>}
            </div>
          </div>
        </div>

        <hr className="section-divider" />

        {/* Products Section */}
        <div className="form-section-content">
          <div className="form-row">
            <div className="form-group full-width" style={{ position: 'relative' }}>
              <label>Search Products:</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search products by name / SKU"
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                disabled={!form.location_id}
              />
              {!form.location_id && <small className="error-text">Select a business location to load products</small>}
            </div>
          </div>

          {errors.products && <div className="error-text mb-2">{errors.products}</div>}

          <div className="product-table" style={{ overflowX: 'auto', maxHeight: '250px', overflowY: 'auto', marginBottom: '1rem', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
            <table style={{ minWidth: '600px', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: '5%' }}>#</th>
                  <th>Available Product</th>
                  <th style={{ width: '18%' }}>SKU</th>
                  <th style={{ width: '18%' }}>Stock</th>
                  <th style={{ width: '15%' }}></th>
                </tr>
              </thead>
              <tbody>
                {!form.location_id ? (
                  <tr><td colSpan={5} className="no-products">Select a location to view products</td></tr>
                ) : isLoading ? (
                  <tr><td colSpan={5} className="no-products">Loading products...</td></tr>
                ) : filteredOptions.length === 0 ? (
                  <tr><td colSpan={5} className="no-products">No products found</td></tr>
                ) : (
                  filteredOptions.map((p, idx) => (
                    <tr key={p.variation_id}>
                      <td style={{ textAlign: 'center', color: '#64748b', fontSize: '0.8rem' }}>{idx + 1}</td>
                      <td style={{ fontWeight: '500', fontSize: '0.875rem' }}>{p.product || p.name}</td>
                      <td>{p.sub_sku || p.sku}</td>
                      <td>{p.stock ?? '-'}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => addProductRow(p)}
                        >
                          Add
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <h4 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Selected Products</h4>
          <div className="product-table" style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: '600px', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: '4%' }}>#</th>
                  <th>Product</th>
                  <th style={{ width: '18%' }}>SKU</th>
                  <th style={{ width: '18%' }}>Alert Quantity</th>
                  <th style={{ width: '18%' }}>Required Quantity</th>
                  <th style={{ width: '4%' }}></th>
                </tr>
              </thead>
              <tbody>
                {form.products.length === 0 ? (
                  <tr><td colSpan={6} className="no-products">No products added</td></tr>
                ) : (
                  form.products.map((p, idx) => (
                    <tr key={p.id}>
                      <td style={{ textAlign: 'center', color: '#64748b', fontSize: '0.8rem' }}>{idx + 1}</td>
                      <td style={{ fontWeight: '500', fontSize: '0.875rem' }}>{p.name}</td>
                      <td>{p.sku}</td>
                      <td>
                        <input
                          type="number"
                          className="form-input"
                          value={p.alertQuantity}
                          min="0"
                          onChange={(e) => updateProduct(p.id, 'alertQuantity', e.target.value)}
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td>
                        <div className="quantity-input-group">
                          <button type="button" className="qty-btn minus" onClick={() => updateProduct(p.id, 'requiredQuantity', Math.max(0, (parseFloat(p.requiredQuantity) || 0) - 1))}>-</button>
                          <input
                            type="number"
                            className="quantity-input-centered"
                            value={p.requiredQuantity}
                            min="0"
                            step="any"
                            onChange={(e) => updateProduct(p.id, 'requiredQuantity', e.target.value)}
                          />
                          <button type="button" className="qty-btn plus" onClick={() => updateProduct(p.id, 'requiredQuantity', (parseFloat(p.requiredQuantity) || 0) + 1)}>+</button>
                        </div>
                      </td>
                      <td><button type="button" className="remove-product-btn" onClick={() => removeProduct(p.id)}>✕</button></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="form-section-content action-buttons-section">
          <div className="action-buttons">
            <button type="submit" className="save-btn" disabled={isSaving}>💾 {isSaving ? 'Saving...' : 'Save'}</button>
            <button type="button" className="save-print-btn" onClick={() => handleSave(true)} disabled={isSaving}>🖨️ Save & Print</button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddPurchaseRequisition;
